import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
  useUpdate,
} from "ra-core";
import { Button } from "@/components/ui/button";

import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";
import { stopFollowUps } from "./followUps";
import { recordLeadEvent, syncDealsToStage } from "./leadEvents";

export const LeadStageControl = () => {
  const record = useRecordContext<Contact>();
  const { dealStages, lostReasons } = useConfigurationContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isPending }] = useUpdate();
  const [pickingReason, setPickingReason] = useState(false);
  if (!record) return null;

  const apply = (stage: string, lostReason?: string) => {
    if (stage === record.stage && !lostReason) return;
    update(
      "contacts",
      {
        id: record.id,
        data: {
          stage,
          lost_reason: stage === "lost" ? lostReason : record.lost_reason,
        },
        previousData: record,
      },
      {
        onSuccess: () => {
          setPickingReason(false);
          const from = dealStages.find((item) => item.value === record.stage);
          const to = dealStages.find((item) => item.value === stage);
          void recordLeadEvent(dataProvider, {
            contact_id: record.id,
            sales_id: record.sales_id,
            type: "stage_change",
            body: `Stage changed from ${from?.label ?? record.stage ?? "New"} to ${to?.label ?? stage}${lostReason ? ` (${lostReason})` : ""}`,
          });
          void syncDealsToStage(dataProvider, record, stage, lostReason);
          void stopFollowUps(dataProvider, record.id, "Stage changed").then(
            () => refresh(),
          );
        },
        onError: () => {
          notify("Could not change the stage", { type: "error" });
        },
      },
    );
  };

  const progress = [
    "new",
    "contacted",
    "appointment-set",
    "negotiation",
    "won",
  ];
  const currentIndex = progress.indexOf(record.stage ?? "");

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-stretch overflow-x-auto">
        {dealStages.map((stage, index) => {
          const isLost = stage.value === "lost";
          const reached =
            !isLost &&
            currentIndex >= 0 &&
            progress.indexOf(stage.value) <= currentIndex;
          const active = record.stage === stage.value;
          return (
            <button
              key={stage.value}
              type="button"
              disabled={isPending}
              onClick={() => {
                if (isLost) {
                  setPickingReason(true);
                  return;
                }
                setPickingReason(false);
                apply(stage.value);
              }}
              className={`relative min-w-0 flex-1 px-3 py-2 text-center text-sm font-medium ${
                index === 0 ? "pl-3" : "pl-5"
              } ${
                active && isLost
                  ? "bg-rose-700 text-white"
                  : reached
                    ? "bg-emerald-600 text-white"
                    : "bg-slate-100 text-slate-600"
              }`}
              style={{
                clipPath:
                  index === 0
                    ? "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)"
                    : "polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)",
                marginRight: index === dealStages.length - 1 ? 0 : -6,
              }}
            >
              {stage.label}
            </button>
          );
        })}
      </div>
      {pickingReason ? (
        <div className="flex flex-wrap gap-1">
          {lostReasons.map((reason) => (
            <Button
              key={reason.value}
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => apply("lost", reason.label)}
            >
              {reason.label}
            </Button>
          ))}
        </div>
      ) : null}
      {record.stage === "lost" && record.lost_reason ? (
        <p className="text-sm text-muted-foreground">
          Lost: {record.lost_reason}
        </p>
      ) : null}
    </div>
  );
};
